import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Trash2, Plus, Minus, ShoppingBag, Truck, Store } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../lib/api';
import { Button } from '../components/ui/button';
import { useSuppliers } from '../hooks/useSuppliers';

const Cart = ({ cart, setCart }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { byId: suppliersById } = useSuppliers();

  const updateQty = (idx, delta) => {
    setCart((prev) => prev.map((it, i) => i === idx ? { ...it, qty: Math.max(1, it.qty + delta) } : it));
  };
  const remove = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));

  const subtotal = cart.reduce((sum, it) => sum + it.variant.price * it.qty, 0);
  const shipping = subtotal >= 25000 ? 0 : 800;
  const total = subtotal + shipping;

  // Regroupe les items par supplier_id en conservant leur index d'origine pour les actions
  const supplierGroups = useMemo(() => {
    const groups = new Map();
    cart.forEach((item, originalIdx) => {
      const sid = item?.variant?.supplier_id || '_unknown';
      if (!groups.has(sid)) groups.set(sid, { supplier_id: sid, items: [], subtotal: 0 });
      const g = groups.get(sid);
      g.items.push({ ...item, originalIdx });
      g.subtotal += item.variant.price * item.qty;
    });
    return Array.from(groups.values());
  }, [cart]);

  if (cart.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <div className="w-20 h-20 rounded-full bg-[#F5F1EA] flex items-center justify-center mx-auto mb-4">
          <ShoppingBag className="w-10 h-10 text-[#1F4D3F]" />
        </div>
        <h1 className="font-serif text-3xl font-semibold text-[#1F4D3F]">{t('cart.empty')}</h1>
        <p className="text-[#7A6F5C] mt-2">Découvrez nos milliers de produits dentaires aux meilleurs prix.</p>
        <Link to="/categories" className="inline-flex mt-6 items-center gap-2 bg-[#1F4D3F] hover:bg-[#173B30] text-white px-6 py-3 rounded-lg font-medium">
          {t('cart.continue_shopping')}
        </Link>
        <div className="mt-8">
          <div className="text-xs uppercase tracking-wider text-[#9A8F82] mb-3">Catégories populaires</div>
          <div className="flex flex-wrap gap-2 justify-center">
            {[
              { label: 'Consommables', id: 'fam_omni' },
              { label: 'Implantologie', id: 'fam_implanto' },
              { label: 'Prothèse', id: 'fam_prothesiste' },
              { label: 'Numérique', id: 'fam_numerique' },
            ].map((c) => (
              <Link key={c.id} to={`/categorie/${c.id}`}
                className="px-4 py-2 bg-white border border-[#E8E0D0] rounded-full text-sm text-[#1F4D3F] hover:bg-[#FAF7F2] transition">
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-4">
        <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-[#2D2A26]">Panier</span>
      </nav>

      <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F] mb-2">Mon panier ({cart.length})</h1>
      {supplierGroups.length > 1 && (
        <p className="text-sm text-[#7A6F5C] mb-6">
          Votre commande sera répartie entre <strong>{supplierGroups.length} fournisseurs</strong>. Les frais de livraison s'appliquent par fournisseur.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-5">
          {supplierGroups.map((group) => {
            const supplier = suppliersById[group.supplier_id];
            const supplierName = supplier?.name || 'Fournisseur';
            const threshold = supplier?.free_shipping_threshold;
            const hasFreeShipping = typeof threshold === 'number' && threshold > 0;
            const freeReached = hasFreeShipping && group.subtotal >= threshold;
            const remaining = hasFreeShipping && !freeReached ? threshold - group.subtotal : 0;
            const progress = hasFreeShipping
              ? Math.min(100, Math.round((group.subtotal / threshold) * 100))
              : 100;
            const supplierShipFee = supplier?.shipping_fee ?? null;

            return (
              <section
                key={group.supplier_id}
                className="bg-white border border-[#E8E0D0] rounded-xl overflow-hidden"
                data-testid={`cart-supplier-group-${group.supplier_id}`}
              >
                {/* Header fournisseur */}
                <header className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E8E0D0] flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-[#1F4D3F] min-w-0">
                    <Store className="w-4 h-4 flex-shrink-0" />
                    <div className="font-medium truncate">{supplierName}</div>
                    {supplier?.city && (
                      <span className="text-xs text-[#7A6F5C] hidden sm:inline">· {supplier.city}</span>
                    )}
                  </div>
                  <div className="text-sm">
                    <span className="text-[#7A6F5C]">Sous-total : </span>
                    <span className="font-semibold text-[#1F4D3F]">{formatPrice(group.subtotal)}</span>
                  </div>
                </header>

                {/* Progress livraison gratuite */}
                {hasFreeShipping && (
                  <div
                    className={`px-4 py-2.5 text-xs border-b border-[#F5F1EA] ${
                      freeReached ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50/60 text-amber-900'
                    }`}
                    data-testid={`cart-shipping-hint-${group.supplier_id}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Truck className="w-3.5 h-3.5 flex-shrink-0" />
                      {freeReached ? (
                        <span><strong>Livraison gratuite atteinte</strong> chez {supplierName}</span>
                      ) : (
                        <span>
                          Encore <strong>{formatPrice(remaining)}</strong> chez {supplierName} pour la livraison gratuite
                          {supplierShipFee != null && supplierShipFee > 0 && (
                            <span className="text-[#7A6F5C] ml-1">(sinon {formatPrice(supplierShipFee)} de frais)</span>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-white border border-[#E8E0D0] overflow-hidden">
                      <div
                        className={`h-full transition-all ${freeReached ? 'bg-emerald-500' : 'bg-[#1F4D3F]'}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Items du groupe */}
                <div className="divide-y divide-[#F5F1EA]">
                  {group.items.map((item) => (
                    <div key={item.originalIdx} className="p-4 flex gap-4">
                      <div className="w-24 h-24 rounded-lg overflow-hidden bg-[#FAF7F2] shrink-0">
                        <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" onError={e => { e.target.style.visibility = 'hidden'; }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-[#7A6F5C] uppercase tracking-wider">{item.product.brand}</div>
                        <div className="font-medium text-[#2D2A26] mt-0.5 truncate">{item.product.name}</div>
                        <div className="text-xs text-[#7A6F5C] mt-1">{item.variant.label}</div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center border border-[#E8E0D0] rounded-lg">
                            <button onClick={() => updateQty(item.originalIdx, -1)} className="w-8 h-8 flex items-center justify-center text-[#1F4D3F] hover:bg-[#FAF7F2]" aria-label="Diminuer"><Minus className="w-3.5 h-3.5" /></button>
                            <span className="w-8 text-center text-sm font-medium">{item.qty}</span>
                            <button onClick={() => updateQty(item.originalIdx, 1)} className="w-8 h-8 flex items-center justify-center text-[#1F4D3F] hover:bg-[#FAF7F2]" aria-label="Augmenter"><Plus className="w-3.5 h-3.5" /></button>
                          </div>
                          <div className="font-serif text-lg font-semibold text-[#1F4D3F]">{formatPrice(item.variant.price * item.qty)}</div>
                        </div>
                      </div>
                      <button onClick={() => remove(item.originalIdx)} className="text-[#7A6F5C] hover:text-[#C75B27] self-start" aria-label="Retirer">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <aside className="bg-white border border-[#E8E0D0] rounded-2xl p-6 h-fit lg:sticky lg:top-[180px]">
          <h2 className="font-serif text-xl font-semibold text-[#1F4D3F] mb-4">Récapitulatif</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-[#5A5247]"><span>{t('cart.subtotal')}</span><span>{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between text-[#5A5247]"><span>{t('cart.shipping')}</span><span>{shipping === 0 ? '✓' : formatPrice(shipping)}</span></div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#F5F1EA] flex justify-between font-serif text-xl text-[#1F4D3F] font-semibold">
            <span>{t('cart.total')}</span><span>{formatPrice(total)}</span>
          </div>
          <Button onClick={() => navigate('/checkout')} className="w-full mt-5 h-11 bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="cart-checkout-btn">{t('cart.checkout')}</Button>
          <Link to="/" className="block text-center mt-3 text-sm text-[#1F4D3F] hover:underline">{t('cart.continue_shopping')}</Link>
        </aside>
      </div>
    </div>
  );
};

export default Cart;
